import { ExpandedSurvey, NavigationConfig, Survey } from "@aidkitorg/types/lib/survey";
import { applyNavigationOverrides, collectAiChatsNode, collectAllApplicantsDashboardNode, collectNavigation, collectOrgManagementNode, collectPaymentsNode, collectSupportDashboardNodes, collectUserManagementNode, indexNavNodesById, NavigationNode, PREVIEW_ALL_ROLES } from "@aidkitorg/types/lib/translation/permissions";
import { expandTemplatesStrict } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { useMemo, useState } from "react";
import { Item } from "../Components/ThreeColumnPage";
import { buildTemplatedComponentPathMap, findAndGoToComponent, templatedComponentKey } from "../ConfigSearch";
import { Switch, useToast } from "@aidkitorg/component-library";
import { DISTRO_DEFAULT_LIGHTNESS, stringToColor } from "../Util";

function navNodeDisplayName(name: NavigationNode['name']): string {
  if (typeof name === 'string') return name || '(unnamed)';
  return name?.en || '(unnamed)';
}

function NavigationPreviewList({ nodes, depth, showSubsurveySections, survey }: { nodes: NavigationNode[], depth: number, showSubsurveySections: boolean, survey: Survey }) {
  const { toast } = useToast();
  return (
    <ul className="space-y-1">
      {nodes.map((n, i) => {
        const nodeColor = stringToColor(n.kind ?? '', DISTRO_DEFAULT_LIGHTNESS);
        return (
          <li key={(n.href || '') + ':' + i} style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
            <div className="flex items-baseline gap-2">
              <span
                className={`rounded-full text-xs px-2 py-1 inline-block mr-1 ${n.tree ? 'cursor-pointer hover:underline' : ''}`}
                style={{ backgroundColor: nodeColor }}
                title={n.tree ? 'Click to scroll to this component in the editor' : undefined}
                onClick={n.tree ? () => findAndGoToComponent(n.tree!, survey) : undefined}
              >
                {n.kind}
              </span>
              <span className={n.bold ? 'font-semibold' : ''}>{navNodeDisplayName(n.name)}</span>
              {n.id && (
                <span
                  className="text-gray-500 font-mono text-[10px] cursor-pointer hover:underline"
                  title="Click to copy ID"
                  onClick={() => {
                    navigator.clipboard.writeText(n.id ?? '');
                    toast({
                      description: 'ID copied to clipboard',
                      variant: 'success',
                      autoClose: true,
                      duration: 2_500,
                    });
                  }}
                >
                  {n.id}
                </span>
              )}

            </div>
            {/* Hide subnodes for Subsurveys, we shouldn't be moving these around separately from their parent */}
            {(n.subnodes && n.subnodes.length > 0) && (n.kind !== 'Subsurvey' || showSubsurveySections) && (
              <div className="mt-1 border-l ml-1" style={{ borderColor: nodeColor }}>
                <NavigationPreviewList nodes={n.subnodes} depth={depth + 1} showSubsurveySections={showSubsurveySections} survey={survey} />
              </div>
            )}
          </li>
        )})}
    </ul>
  );
}

// collectNavigation gates node kinds by scope: "Global" yields Collections + Dashboards;
// "Applicant" yields Subsurveys, Sections, and applicant pages. Distro authors care about both
// views, so the preview lets them toggle between them.
const NAV_PREVIEW_VIEWS = ['Logical', 'Visual'] as const;
type NavPreviewView = typeof NAV_PREVIEW_VIEWS[number];

const NAV_PREVIEW_SCOPES = ['Applicant', 'Global'] as const;
type NavPreviewScope = typeof NAV_PREVIEW_SCOPES[number];

// `selectedConfigIndex` chooses which NavigationConfig's override tree to apply. The backend picks
// this per-applicant by evaluating each config's `condition`; the preview can't evaluate conditions
// without a real applicant, so the author picks a config explicitly here. `null` previews the bare
// auto-derived nav with no overrides.
function resolveTemplatedNavTrees(node: NavigationNode, templatedPaths: Record<string, string[]>): void {
  if (node._parentTemplatedBlockId) {
    const key = node._preSubstitutionIdentifier
      ? templatedComponentKey(node._parentTemplatedBlockId, node._preSubstitutionIdentifier)
      : undefined;
    node.tree = key ? templatedPaths[key] : undefined;
  }
  node.subnodes?.forEach((child) => resolveTemplatedNavTrees(child, templatedPaths));
}

function resolveNavPreview(expandedSurvey: ExpandedSurvey, scope: NavPreviewScope, selectedConfigIndex: number | null,): NavigationNode | null {
  // Expand templated blocks first so Subsurveys/Sections nested inside them are walked — this
  // mirrors the backend's programNavigation, which collects nav from the expanded program.
  const expanded = expandTemplatesStrict<ExpandedSurvey>(expandedSurvey);
  const applicantUid = scope === 'Applicant' ? '[uid]' : undefined;
  const collected = collectNavigation(
    { kind: 'Collection', name: { en: '' }, components: expanded.survey, options: {} },
    scope,
    // Applicant scope derives hrefs from an applicant uid; a placeholder suffices for a preview.
    applicantUid,
  );
  if (!collected) return null;
  resolveTemplatedNavTrees(collected, buildTemplatedComponentPathMap(expandedSurvey.survey));
  // Program-level pages (User Management, Payments, ...) that live outside the survey tree but are
  // still valid override targets — same functions `programNavigation` uses on the backend, spliced
  // into the same positions: the All-Applicants dashboard nests at the end of the Collections tree,
  // Payments renders ahead of everything, the rest append to the flat tail.
  if (expanded.config) {
    const config = expanded.config;
    const dashboardAllNode = collectAllApplicantsDashboardNode({ roles: PREVIEW_ALL_ROLES, components: expanded.survey });
    if (dashboardAllNode) collected.subnodes = [...(collected.subnodes ?? []), dashboardAllNode];

    const tail = [
      ...collectSupportDashboardNodes({ config, roles: PREVIEW_ALL_ROLES }),
      collectUserManagementNode({ config, roles: PREVIEW_ALL_ROLES }),
      collectOrgManagementNode({ config, roles: PREVIEW_ALL_ROLES }),
      collectAiChatsNode({ config, roles: PREVIEW_ALL_ROLES, applicantUid }),
    ].filter((n): n is NavigationNode => !!n);
    const paymentsNode = collectPaymentsNode({ roles: PREVIEW_ALL_ROLES, applicantUid });

    collected.subnodes = [
      ...(paymentsNode ? [paymentsNode] : []),
      ...(collected.subnodes ?? []),
      ...tail,
    ];
  }
  const config = selectedConfigIndex == null ? undefined : expandedSurvey.navigation?.[selectedConfigIndex];
  if (config && config.scope === scope) {
    return applyNavigationOverrides(collected, config.nodes);
  }
  return collected;
}

/**
 * Indexes every auto-derived nav node (across both Global and Applicant scope, since a `target`
 * on a Navigation Reference Node may point into either) by its `navNodeIdentifier`. Used by the
 * `NavigationTargetPreview` customUI to resolve an opaque `target` id/href/name-path back to the
 * node it refers to, so authors can see what it actually is instead of just a raw string.
 */
export function buildNavTargetIndex(expandedSurvey: ExpandedSurvey): Map<string, NavigationNode> {
  const index = new Map<string, NavigationNode>();
  for (const scope of NAV_PREVIEW_SCOPES) {
    const root = resolveNavPreview(expandedSurvey, scope, null);
    if (!root) continue;
    indexNavNodesById(root).forEach((node, id) => {
      if (!index.has(id)) index.set(id, node);
    })
  }
  return index;
}

function configLabel(config: NavigationConfig, index: number): string {
  const name = typeof config.name === 'string' ? config.name : config.name?.en;
  return `${name || `Config ${index + 1}`}${config.condition ? '' : ' (default)'}`;
}

// An unconditional config always matches, so any config authored after it can never be selected for
// an applicant. Flag the first such shadowing config so authors notice before publishing.
function shadowingWarning(configs: NavigationConfig[] | undefined, scope: NavPreviewScope): string | null {
  if (!configs?.length) return null;
  let showWarning = false;
  let firstShadowingConfigIndex = -1;
  let foundSubsequentConfigWithoutCondition = false;
  for (let i = 0; i < configs.length; i++) {
    const config = configs[i];
    // If we've already found an applicant config without a condition, and we're now finding another, show a warning.
    if (config.scope === scope && foundSubsequentConfigWithoutCondition) {
      showWarning = true;
      break;
    }
    // If we haven't yet found an applicant config without a condition, and the current one doesn't have a condition, set the flag.
    if (config.scope === scope && !config.condition && !foundSubsequentConfigWithoutCondition) {
      foundSubsequentConfigWithoutCondition = true;
      firstShadowingConfigIndex = i;
    }
  };
  if (!showWarning) return null;
  return `"${configLabel(configs[firstShadowingConfigIndex], firstShadowingConfigIndex)}" has no condition but isn't last — it always matches, so any ${scope} scope configs after it will never apply. Move unconditional configs last.`;
}

/**
 * Renders the fully-resolved program navigation tree with any user overrides applied. Read-only
 * preview — edits happen on the Navigation array field itself; this view just confirms the result.
 * The scope toggle switches between the applicant-context sidebar and the global view.
 */
export function NavigationPreview({ expandedSurvey }: { expandedSurvey: ExpandedSurvey }) {
  const [scope, setScope] = useState<NavPreviewScope>('Applicant');
  // null = preview the bare auto-derived nav (no overrides); a number indexes expandedSurvey.navigation.
  const [selectedConfigIndex, setSelectedConfigIndex] = useState<number | null>(null);
  const [view, setView] = useState<NavPreviewView>('Logical');
  const [showSubsurveySections, setShowSubsurveySections] = useState(false);

  const configs = expandedSurvey.navigation;
  const warnings = useMemo(() => ({ applicant: shadowingWarning(configs, 'Applicant'), global: shadowingWarning(configs, 'Global') }), [configs]);

  // Clamp the selection if configs were added/removed out from under us.
  const effectiveConfigIndex = selectedConfigIndex !== null && configs?.[selectedConfigIndex] ? selectedConfigIndex : null;

  const resolved = useMemo(() => {
    try {
      return resolveNavPreview(expandedSurvey, scope, effectiveConfigIndex);
    } catch (e) {
      console.error('Error resolving navigation preview', e);
      return null;
    }
  }, [expandedSurvey, scope, effectiveConfigIndex]);

  const top = resolved?.subnodes ?? [];
  return (
    <div className="w-full p-3 text-xs">
      <div className="text-xs font-semibold text-blue-700 mb-2">Navigation Preview Settings</div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-1">
          {NAV_PREVIEW_VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`text-xs rounded px-2 py-0.5 border ${view === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-700 border-indigo-300'}`}
            >
              {v}
            </button>
          ))}
          <select
            value={effectiveConfigIndex == null ? '' : String(effectiveConfigIndex)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              const newIndex = e.target.value === '' ? null : Number(e.target.value);
              setSelectedConfigIndex(newIndex);
              if (newIndex != null) {
                setScope(configs?.[newIndex]?.scope ?? 'Applicant');
              }
            }}
            className="text-xs rounded px-2 py-0.5 border bg-white text-blue-700 border-blue-300"
          >
            <option value="">Without overrides</option>
            {(configs ?? []).map((c, i) => (
              <option key={i} value={String(i)}>{configLabel(c, i)}</option>
            ))}
          </select>
          {NAV_PREVIEW_SCOPES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={`text-xs rounded px-2 py-0.5 border ${selectedConfigIndex === null ? (scope === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300') : configs?.[selectedConfigIndex]?.scope === s ? 'bg-gray-600 text-white border-gray-600' : 'bg-white text-gray-700 border-gray-300'}`}
              disabled={effectiveConfigIndex !== null && configs?.[effectiveConfigIndex]?.scope !== s}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      {view === 'Logical' && (
        <div className="flex items-center px-2">
          <Switch
            size="sm"
            checked={showSubsurveySections}
            onCheckedChange={setShowSubsurveySections}
            label="Show Sections under Subsurveys"
          />
        </div>
      )}
      <div className="text-[10px] text-gray-500 mb-2">
        {scope === 'Applicant'
          ? 'Applicant view — Subsurveys, Sections, and applicant pages. The backend picks an Applicant config per applicant by its condition; here you choose one to preview.'
          : 'Global view — Collections and Dashboards. Conditions can\'t be evaluated without an applicant, so only the first Global config with no condition applies in this scope.'}
      </div>
      {warnings.applicant && (
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          ⚠ {warnings.applicant}
        </div>
      )}
      {warnings.global && (
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          ⚠ {warnings.global}
        </div>
      )}
      {!resolved ? (
        <div className="text-red-600">Unable to resolve navigation preview.</div>
      ) : top.length === 0 ? (
        <div className="text-gray-500 italic">No navigation entries resolved.</div>
      ) : view === 'Visual' ? (
        <div className="space-y-1">
          {top.map((n, i) => <Item key={(n.href || '') + ':' + i} item={n} depth={0} />)}
        </div>
      ) : (
        <NavigationPreviewList nodes={top} depth={0} showSubsurveySections={showSubsurveySections} survey={expandedSurvey.survey} />
      )}
    </div>
  );
}
